<script lang="ts" setup>
import LikeDislike from '@/vue/molecules/like-dislike.vue';
import {ref, watch} from 'vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {ReccoRatingDTO} from '@/ts/types/dto/recco/rating.dto';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';
import {useBookmarks} from '@/ts/composables/stateful/use-bookmarks';
import {useFeatureAccess} from '@/ts/composables/stateful/use-feature-access';
import {useSnackbarStore, type SnackbarAction} from '@/ts/store/snackbar-store';
import {EXPLORE_SAVED_QUERY, pagesPath} from '@/ts/router/pages-path';
import {useI18n} from 'vue-i18n';

const api = useApi();
const {updateBookmark} = useBookmarks();
const {canAccessExplore} = useFeatureAccess();
const snackbarStore = useSnackbarStore();
const {t} = useI18n();

const props = defineProps<{
  blockContent: ComponentContent;
}>();

const emit = defineEmits<{
  contentUpdated: [content: ComponentContent];
}>();

// Convert between block content rating format and recco rating format
function blockRatingToReccoRating(blockRating?: 'like' | 'dislike' | 'unrated'): ReccoRatingDTO {
  if (!blockRating || blockRating === 'unrated') {return ReccoRatingDTO.NOT_RATED;}
  if (blockRating === 'like') {return ReccoRatingDTO.LIKE;}
  return ReccoRatingDTO.DISLIKE;
}

function reccoRatingToBlockRating(reccoRating: ReccoRatingDTO): 'like' | 'dislike' | 'unrated' {
  if (reccoRating === ReccoRatingDTO.LIKE) {return 'like';}
  if (reccoRating === ReccoRatingDTO.DISLIKE) {return 'dislike';}
  return 'unrated';
}

// Local state for rating and bookmark
const rating = ref<ReccoRatingDTO>(blockRatingToReccoRating(props.blockContent.rating));
const isBookmarked = ref<boolean>(props.blockContent.isBookmarked ?? false);

// Watch for prop changes
watch(() => props.blockContent.rating, (newRating) => {
  rating.value = blockRatingToReccoRating(newRating);
});

watch(() => props.blockContent.isBookmarked, (newBookmarked) => {
  isBookmarked.value = newBookmarked ?? false;
});

// API calls
const [putRating, putRatingInProgress] = useLoadingNotification(
  async(ratingData: ReccoRatingDTO) => {
    const blockRating = reccoRatingToBlockRating(ratingData);
    await api.me.postBlockContentRating(props.blockContent.id, {rating: blockRating});
  },
  (_, ratingData: ReccoRatingDTO): void => {
    rating.value = ratingData;
    // Update the parent content
    const updatedContent: ComponentContent = {
      ...props.blockContent,
      rating: reccoRatingToBlockRating(ratingData),
    };
    emit('contentUpdated', updatedContent);
  },
);

/**
 * Says where a saved item went (IPD-3328). Only on saving: un-saving is the reader taking something
 * away, and the button's own state already says it is gone. The way to the bookmarks is Explore's
 * Saved results, so a reader without Explore is told it was saved and offered nothing to follow.
 */
function confirmBookmarked(): void {
  const action: SnackbarAction = {
    label: t('snackbar_bookmark_action'),
    to: {path: pagesPath.main.explore, query: EXPLORE_SAVED_QUERY},
  };
  snackbarStore.show({
    message: t('snackbar_bookmark_saved'),
    ...canAccessExplore.value ? {action} : {},
    dismissible: true,
  });
}

const [putBookmark, putBookmarkInProgress] = useLoadingNotification(
  // Goes through the composable rather than calling the endpoint and poking the store here:
  // the store's toggle count is what syncs Explore's Saved results (IPD-2938), so a second
  // hand-rolled path is a bookmark surface that silently leaves Saved stale.
  async(bookmarked: boolean) => {
    await updateBookmark({...props.blockContent, isBookmarked: bookmarked}, bookmarked);
  },
  (_, bookmarked: boolean): void => {
    isBookmarked.value = bookmarked;
    // Update the parent content
    emit('contentUpdated', {...props.blockContent, isBookmarked: bookmarked});
    if (bookmarked) {
      confirmBookmarked();
    }
  },
);


// Event handlers for the like-dislike component
function handleLike(): void {
  const newRating = rating.value === ReccoRatingDTO.LIKE ? ReccoRatingDTO.NOT_RATED : ReccoRatingDTO.LIKE;
  if (!putRatingInProgress.value) {
    void putRating(newRating);
  }
}

function handleDislike(): void {
  const newRating = rating.value === ReccoRatingDTO.DISLIKE ? ReccoRatingDTO.NOT_RATED : ReccoRatingDTO.DISLIKE;
  if (!putRatingInProgress.value) {
    void putRating(newRating);
  }
}

function handleBookmark(): void {
  if (!putBookmarkInProgress.value) {
    void putBookmark(!isBookmarked.value);
  }
}
</script>

<template>
  <like-dislike
    :rating="rating"
    :bookmarked="isBookmarked"
    :loading-like="putRatingInProgress"
    :loading-bookmarks="putBookmarkInProgress"
    @like="handleLike"
    @dislike="handleDislike"
    @bookmark="handleBookmark"
  />
</template>